import { useState } from 'react';
import { Button } from '@dreamtree-org/twreact-ui';
import { Download } from 'lucide-react';
import { PageHeader, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import { toCsv, downloadText } from '../lib/csv';
import { formatDateTime, humanize } from '../lib/format';

const AREAS = [
  { value: 'all', label: 'All areas' },
  { value: 'merchant', label: 'Merchants' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'moderation', label: 'Moderation' },
  { value: 'exceptions', label: 'Exceptions' },
  { value: 'policy', label: 'Policies' },
  { value: 'plan', label: 'Plans' },
  { value: 'billing', label: 'Billing' },
  { value: 'admin', label: 'Admins' },
  { value: 'reconciliation', label: 'Reconciliation' },
];

/**
 * Platform audit log (append-only: there is no edit or delete path). Area and
 * search filter on the server; the newest 500 matching events are shown.
 */
export default function Audit() {
  const [q, setQ] = useState('');
  const [action, setAction] = useState('all');
  const events = useQuery('platformAudit', { q: q.trim(), action, limit: 500 });

  const rows = (events.data && events.data.data) || [];
  const exportCsv = () => downloadText('platform-audit.csv', toCsv(rows, [
    { key: 'at', label: 'At (UTC)' }, { key: 'actor', label: 'Actor' }, { key: 'action', label: 'Action' },
    { key: 'target', label: 'Target' }, { key: 'company_slug', label: 'Merchant' }, { key: 'diff', label: 'Details', value: (r) => (r.diff ? JSON.stringify(r.diff) : '') },
  ]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        subtitle="Every platform mutation, append-only. Nothing here can be edited or deleted."
        actions={<Button variant="outline" leftIcon={<Download size={16} />} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>}
      />
      <SectionCard>
        <FilterBar q={q} onQ={setQ} placeholder="Actor, action, target…" filters={[{ key: 'action', label: 'Area', value: action, options: AREAS }]} onFilter={(_, v) => setAction(v)} />
        <QueryState query={events}>
          {(body) => (body.data.length ? (
            <>
            <p className="text-xs opacity-60">{body.totalCount > body.data.length ? `Newest ${body.data.length} of ${body.totalCount} events` : `${body.totalCount} events`}</p>
            <DataTable
              data={body.data}
              pagination={rows.length > 25}
              columns={[
                { key: 'at', label: 'When', render: (r) => formatDateTime(r.at) },
                { key: 'action', label: 'Action', render: (r) => <span className="font-medium">{humanize(r.action)}</span> },
                { key: 'target', label: 'Target', render: (r) => <span className="break-all">{r.target}</span> },
                { key: 'actor', label: 'Actor' },
                { key: 'company_slug', label: 'Merchant', hideOnMobile: true, render: (r) => r.company_slug || '—' },
                { key: 'diff', label: 'Details', hideOnMobile: true, wrap: true, render: (r) => (r.diff ? <code className="text-xs break-all">{JSON.stringify(r.diff)}</code> : '—') },
              ]}
            />
            </>
          ) : <EmptyState title="No events match">Try a different area or search.</EmptyState>)}
        </QueryState>
      </SectionCard>
    </div>
  );
}
